import { useState, useRef, useEffect } from 'react';
import { MessageSquarePlus, Send, X } from 'lucide-react';
import { frameOrderApi } from '../../services/frameOrderApi';

interface InlineCommentInputProps {
    documentId: string;
    onCommentAdded: () => void;
}

export function InlineCommentInput({ documentId, onCommentAdded }: InlineCommentInputProps) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            inputRef.current?.focus();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                handleClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleClose = () => {
        setOpen(false);
        setText('');
    };

    const handleSubmit = async () => {
        if (!text.trim() || submitting) return;

        setSubmitting(true);
        try {
            await frameOrderApi.addComment(documentId, text.trim());
            setText('');
            setOpen(false);
            onCommentAdded();
        } catch (err) {
            console.error('Failed to add comment:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
        if (e.key === 'Escape') {
            handleClose();
        }
    };

    if (!open) {
        return (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
                className="rounded p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                title="Add comment"
            >
                <MessageSquarePlus className="h-4 w-4" />
            </button>
        );
    }

    return (
        <div
            ref={containerRef}
            className="absolute right-0 top-full z-20 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center gap-1.5">
                <input
                    ref={inputRef}
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add a comment..."
                    maxLength={2000}
                    className="h-8 flex-1 rounded-md border border-gray-300 px-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                    onClick={handleSubmit}
                    disabled={!text.trim() || submitting}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    title="Send"
                >
                    <Send className="h-3.5 w-3.5" />
                </button>
                <button
                    onClick={handleClose}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Cancel"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
            <p className="mt-1 text-right text-[10px] text-gray-400">Enter to send, Esc to cancel</p>
        </div>
    );
}
